import React, { useEffect, useState } from 'react';
import { Database } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { loadClientInventory } from '../../services/inventoryApi';
import { buildInventorySummary } from './inventorySummary';
import InventoryDownload from './InventoryDownload';
import { captureKey } from '../../services/inventoryIdentity';

export default function ClientInventorySummary({ sapIds, refreshKey }) {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('ops.inventario.view');
  const idsKey = JSON.stringify(sapIds);
  const [state, setState] = useState({ key: '', loading: false, rows: [], error: '' });
  const key = `${idsKey}:${refreshKey}`;

  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    const ids = JSON.parse(idsKey);
    setState({ key, loading: Boolean(ids.length), rows: [], error: '' });
    if (ids.length) {
      loadClientInventory(ids, controller.signal).then(rows => {
        if (!controller.signal.aborted) setState({ key, loading: false, rows, error: '' });
      }).catch(err => {
        if (!controller.signal.aborted) {
          console.error('Error al cargar inventarios del cliente:', err);
          setState({ key, loading: false, rows: [], error: err.message });
        }
      });
    }
    return () => controller.abort();
  }, [allowed, idsKey, key]);

  if (!allowed) return null;
  const loading = state.key !== key || state.loading;
  return (
    <section className="rounded-lg border border-violet-300/20 bg-[#201437]/80 p-5">
      <h2 className="flex items-center gap-2 font-semibold"><Database size={18} className="text-emerald-300" />Resumen de inventario</h2>
      <p className="mt-2 text-xs leading-5 text-violet-200/65">Última captura registrada por nube y cuenta vinculada a los IDs SAP. Las fechas pueden diferir entre cuentas. Datos históricos de BigQuery/GCS, no una consulta en vivo ni un diagnóstico de salud.</p>
      {loading ? <p role="status" className="mt-3 text-sm">Analizando últimos inventarios...</p>
        : state.error ? <p role="alert" className="mt-3 text-sm text-red-200">{state.error}</p>
          : !state.rows.length ? <p className="mt-3 text-sm text-violet-200/70">Sin inventario registrado para las cuentas vinculadas a este cliente. Esto no significa que no tenga recursos cloud.</p>
            : <div className="mt-4 space-y-5">{state.rows.map(row => {
              const meta = row.metadata;
              const date = new Date(meta.fecha_creacion).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', hourCycle: 'h23' });
              return <article key={captureKey(meta)} className="space-y-3 border-t border-violet-300/15 pt-4">
                <h3 className="font-semibold">{meta.proveedor} · {meta.nombre_cliente}</h3>
                <p className="break-all text-xs text-violet-200/70">Cuenta: {meta.numero_cliente} · {meta.region} · {date} (México)</p>
                {row.estado === 'error' ? <p role="alert" className="text-sm text-red-200">{row.error} No se presenta un conteo parcial ni se sustituye por una captura anterior.</p>
                  : <p className="text-sm leading-7 text-violet-50/90">{buildInventorySummary(meta, row, date)}</p>}
                <InventoryDownload key={`${key}:${captureKey(meta)}`} inventoryId={meta.id} inventory={meta} />
              </article>;
            })}</div>}
      <p className="mt-3 text-xs text-violet-200/55">Resumen automático por reglas y conteos completos de recursos reconocidos, sin IA.</p>
    </section>
  );
}
